<template>
  <div class="branch-management-container">
    <!-- Page Header & Action -->
    <div class="page-header py-4">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">Manage Branches</h1>
          <p class="page-subtitle">Configure and monitor your physical business locations</p>
        </div>
        <div class="header-right d-flex gap-3">
          <button class="btn btn-refresh-header" @click="load_state()" :disabled="page.isLoading">
            <i class="fas fa-sync-alt me-2" :class="{ 'fa-spin': page.isLoading }"></i>
            Refresh
          </button>
          <router-link v-if="write" :to="{ name: 'ExtranetBranchForm' }" class="btn-primary-ext">
            <i class="fas fa-plus me-2"></i>
            Add New Branch
          </router-link>
        </div>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="validation" class="alert-box-ext mb-4">
      <i class="fas fa-exclamation-circle me-2"></i>
      {{ validation }}
    </div>

    <!-- Main Card Container -->
    <div class="management-card" v-if="thisComponentShow">
      <!-- Filter Bar -->
      <div class="filter-bar">
        <div class="filter-left">
          <!-- Search Control -->
          <div class="search-box-custom">
            <div class="search-input-wrapper">
              <input 
                type="text" 
                class="form-control-search" 
                v-model="searchCriteria" 
                placeholder="Search by name or code..." 
                @keyup.enter="handleSearch" 
              />
              <button class="btn-search" @click="handleSearch">
                <i class="fas fa-search"></i>
              </button>
            </div>
          </div>

          <!-- Status Filter -->
          <div class="filter-dropdown-group">
            <select v-model="selectedStatus" class="form-select-ext" @change="handleSearch">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        
        <div class="filter-right">
          <div class="stats-summary">
            <span class="stats-label">Total Branches:</span>
            <span class="stats-value">{{ meta.total_count || 0 }}</span>
          </div>
          <button class="btn-reset-primary" @click="performClearSearch" title="Reset Filters">
            <i class="fas fa-undo-alt"></i>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="card-content">
        <!-- Loading State -->
        <div v-if="page.isLoading && branches.length === 0" class="loading-state">
          <div class="loader-box">
            <div class="spinner-border text-danger" role="status"></div>
            <p>Loading branch records...</p>
          </div>
        </div>

        <!-- Table Content -->
        <div v-else class="table-outer">
          <div class="table-responsive">
            <table class="modern-list-table">
              <thead>
                <tr>
                  <th width="60" class="col-no">No</th>
                  <th width="120">Status</th>
                  <th width="120">Code</th>
                  <th width="200">Branch Name</th>
                  <th width="250">Address</th>
                  <th width="150">Phone</th>
                  <th class="text-end pe-4" width="150">Actions</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="branches.length === 0">
                  <tr>
                    <td colspan="7">
                      <div class="empty-list">
                        <template v-if="isFiltered">
                          <div class="empty-art filtered">
                            <i class="fas fa-search"></i>
                          </div>
                          <h3>No Matches Found</h3>
                          <p>We couldn't find any branches matching your current filters. Try adjusting your search query or status selection.</p>
                          <button class="btn-clear-empty mt-4" @click="performClearSearch">
                            <i class="fas fa-eraser me-2"></i>
                            Clear All Filters
                          </button>
                        </template>
                        <template v-else>
                          <div class="empty-art">
                            <i class="fas fa-map-marker-alt"></i>
                          </div>
                          <h3>No Branches Registered</h3>
                          <p>This tenant cluster doesn't have any physical business branches registered yet.</p>
                          <router-link v-if="write" :to="{ name: 'ExtranetBranchForm' }" class="btn-create-empty mt-4">
                            <i class="fas fa-plus me-2"></i>
                            Register First Branch
                          </router-link>
                        </template>
                      </div>
                    </td>
                  </tr>
                </template>

                <template v-else v-for="(item, index) in branches" :key="item.id || index">
                  <tr :class="{ 'inactive-row': item.deleted_at }">
                    <td class="col-no">{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                    <td>
                      <span class="status-pill" :class="item.deleted_at ? 'inactive' : 'active'">
                        <span class="status-dot"></span>
                        {{ item.deleted_at ? 'Inactive' : 'Active' }}
                      </span>
                    </td>
                    <td>
                      <span class="code-pill">{{ item.code }}</span>
                    </td>
                    <td>
                      <div class="branch-name-box">
                        <span class="branch-name">{{ item.name }}</span>
                        <small class="profile-id">ID: {{ item.business_profile?.id || '-' }}</small>
                      </div>
                    </td>
                    <td>
                      <div class="address-box" :title="item.business_profile?.address">
                        {{ item.business_profile?.address || '-' }}
                      </div>
                    </td>
                    <td>
                      <span class="phone-text">
                        <i class="fas fa-phone-alt me-2 text-muted small"></i>
                        {{ item.business_profile?.phone || '-' }}
                      </span>
                    </td>
                    <td class="text-end pe-4 text-nowrap">
                      <div class="d-flex justify-content-end gap-2">
                        <router-link 
                          v-if="write" 
                          :to="{ name: 'ExtranetBranchEdit', params: { id: item.id } }"
                          class="action-btn edit" 
                          title="Edit Branch"
                        >
                          <i class="fas fa-edit"></i>
                        </router-link>
                        <button 
                          v-if="write" 
                          @click.prevent="confirmDelete(item)" 
                          class="action-btn delete" 
                          title="Delete Branch"
                        >
                          <i class="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Footer / Pagination -->
      <div class="card-footer" v-if="page.output && branches.length > 0">
        <o-pagination 
          :page="meta.page"  
          :total="meta.total" 
          @callback="navPage"
        />
      </div>
    </div>

    <!-- Warning Modal -->
    <o-warning-box
      v-if="isWarningVisible"
      :title="'Unable to Delete'"
      :message="warningMessage"
      @cancel="closeWarning()"
      :hide-action="true"
    />

    <!-- Delete Confirmation Modal -->
    <o-warning-box
      v-if="isDeleteConfirmVisible"
      title="Delete Branch"
      :item-name="itemToDelete?.name || 'this branch'"
      warning-message="This action cannot be undone."
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :is-loading="page.isLoading"
      @confirm="handleDelete"
      @cancel="closeDeleteConfirm"
    />
  </div>
</template>

<script>
import store from '@/store/'
import { mapState } from 'vuex'
import { OPagination, OWarningBox } from '@/components/OmnihControl'
import { onMounted, reactive, ref, computed, watch, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'

export default {
  name: 'ExtranetBranch',

  components: {
    OPagination,
    OWarningBox
  },

  setup() {
    const router = useRouter()
    let lastActivePage = 1

    // Local Search State
    const searchCriteria = ref('')
    const selectedStatus = ref('')

    let meta = computed(() => store.state.ExtranetBranch.meta)

    let page = reactive({
      isLoading: false,
      output: false,
      component: 'branch'
    })

    const selfLoaded = ref(store.state.Auth.selfLoaded)

    watch(
      () => store.state.Auth.selfLoaded,
      (newVal) => {
        if (newVal) {
          selfLoaded.value = newVal
          load_state()
        }
      }
    )

    onMounted(() => {
      if (selfLoaded.value) {
        load_state()
      }
    })

    onBeforeUnmount(() => {
      const updatedMeta = { ...meta.value, page: lastActivePage }
      store.commit('ExtranetBranch/switch_page', { meta: updatedMeta })
      store.commit('ExtranetBranch/clear_search')
    })

    const thisComponentShow = ref(true)
    const validation = ref('')
    const isWarningVisible = ref(false)
    const warningMessage = ref('')

    const write = ref(false)

    const currentTenantName = computed(() => {
      return store.state.extranet.selectedTenant?.name || 'Selected Tenant'
    })

    const handleChangeTenant = () => {
      router.push({ name: 'ExtranetTenantAdmin' })
    }

    function load_state() {
      page.isLoading = true

      store.dispatch('Auth/getBusinessProfile').then(businessProfile => {
        const filter = {
          corporate_id: businessProfile.corporate_id
        }

        store.commit('ExtranetBranch/add_filter', { filter })

        store.dispatch('ExtranetBranch/read')
          .then(() => {
            page.isLoading = false
            page.output = true
          })
          .catch((err) => {
            validation.value = err.response?.data?.message || 'Failed to load branches'
            thisComponentShow.value = false
            page.isLoading = false
            page.output = true
          })

        if (store.state.Auth.currUser.level === 'root') {
          write.value = true
        } else {
          const userModules = store.state.Auth.userInfo.modules
          if (userModules) {
            const matchingModule = userModules.find(module => module.module_name === page.component)
            write.value = matchingModule ? matchingModule.write : false
          } else {
            write.value = false
          }
        }
      })
    }

    function handleSearch() {
      let search = {}
      if (searchCriteria.value) {
        search['keyword'] = searchCriteria.value
      }
      
      if (selectedStatus.value) {
        search['status'] = selectedStatus.value
      }

      lastActivePage = meta.value.page
      const updatedMeta = { ...meta.value, page: 1 }
      store.commit('ExtranetBranch/switch_page', { meta: updatedMeta })
      store.commit('ExtranetBranch/set_search', { search })
      store.dispatch('ExtranetBranch/read').then(() => {})
    }

    function performClearSearch() {
      searchCriteria.value = ''
      selectedStatus.value = ''
      store.commit('ExtranetBranch/clear_search')
      store.dispatch('ExtranetBranch/read').then(() => {})
    }

    const isDeleteConfirmVisible = ref(false)
    const itemToDelete = ref(null)

    const confirmDelete = (item) => {
      isWarningVisible.value = false
      warningMessage.value = ''
      const msg = 'This branch cannot be deleted because it still has associated merchants. Please remove or reassign the merchants first.'

      if (item.merchant_count > 0) {
        warningMessage.value = msg
        isWarningVisible.value = true
        return
      }

      itemToDelete.value = item
      isDeleteConfirmVisible.value = true
    }

    const closeDeleteConfirm = () => {
      isDeleteConfirmVisible.value = false
      itemToDelete.value = null
    }

    const handleDelete = () => {
      if (!itemToDelete.value) return

      page.isLoading = true
      const id = itemToDelete.value.id

      const currentPage = meta.value.page
      const currentBranchesCount = store.state.ExtranetBranch.branches.length

      store.dispatch('ExtranetBranch/delete', id)
        .then(() => {
          if (currentBranchesCount === 1 && currentPage > 1) {
            const newPage = currentPage - 1
            lastActivePage = newPage
            const _meta = { ...meta.value, page: newPage }
            store.commit('ExtranetBranch/switch_page', { meta: _meta })
          }
          load_state()
        }).catch(() => {
          const msg = 'This branch cannot be deleted because it still has associated merchants. Please remove or reassign the merchants first.'
          warningMessage.value = msg
          isWarningVisible.value = true
        }).finally(() => {
          page.isLoading = false
          closeDeleteConfirm()
        })
    }

    function navPage(pageNum) {
      lastActivePage = pageNum
      const _meta = { ...meta.value, page: pageNum }
      store.commit('ExtranetBranch/switch_page', { meta: _meta })
      load_state()
    }

    function closeWarning() {
      isWarningVisible.value = false
    }

    const isFiltered = computed(() => {
      return !!searchCriteria.value || !!selectedStatus.value
    })

    return {
      page,
      load_state,
      meta,
      navPage,
      searchCriteria,
      selectedStatus,
      handleSearch,
      performClearSearch,
      validation,
      thisComponentShow,
      write,
      selfLoaded,
      isWarningVisible,
      warningMessage,
      closeWarning,
      confirmDelete,
      handleDelete,
      closeDeleteConfirm,
      isDeleteConfirmVisible,
      currentTenantName,
      handleChangeTenant,
      isFiltered
    }
  },

  computed: {
    ...mapState({
      branches: state => state.ExtranetBranch.branches,
    }),
  }
}
</script>

<style scoped>
.branch-management-container {
    max-width: 1440px;
    margin: 0 auto;
}

/* Page Header */
.page-header {
    margin-bottom: 8px;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 26px;
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 4px 0;
    letter-spacing: -0.5px;
}

.page-subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0;
}

/* Primary Ext Button */
.btn-primary-ext {
    padding: 12px 24px;
    background: var(--extranet-primary);
    color: white;
    border-radius: var(--extranet-rounded);
    font-weight: 700;
    font-size: 14px;
    text-decoration: none;
    display: flex;
    align-items: center;
    transition: all 0.2s;
    border: none;
    box-shadow: 0 4px 12px rgba(252, 102, 102, 0.2);
}

.btn-primary-ext:hover {
    filter: brightness(0.95);
    transform: translateY(-1px);
    color: white;
}

.btn-refresh-header {
    padding: 10px 20px;
    background: white;
    border: 1px solid var(--extranet-accent);
    color: #64748b;
    border-radius: var(--extranet-rounded);
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    transition: all 0.2s;
}

.btn-refresh-header:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #334155;
}

/* Context Bar */
.context-bar {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
    background: linear-gradient(to right, #ffffff, #f8faff);
}

.context-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
}

.tenant-info {
    display: flex;
    align-items: center;
    gap: 16px;
}

.context-icon-box {
    width: 44px;
    height: 44px;
    background: var(--extranet-primary);
    background: linear-gradient(135deg, #fc6666 0%, #ff8e8e 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    box-shadow: 0 4px 10px rgba(252, 102, 102, 0.2);
}

.context-details {
    display: flex;
    flex-direction: column;
}

.context-label {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

.context-value {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
}

.btn-context-action {
    background: white;
    color: #64748b;
    border: 1px solid var(--extranet-accent);
    padding: 8px 16px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
}

.btn-context-action:hover {
    background: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

/* Management Card */
.management-card {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
}

/* Filter Bar */
.filter-bar {
    padding: 24px 32px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    background: #ffffff;
    border-bottom: 1px solid #f1f5f9;
    gap: 24px;
}

.filter-left {
    display: flex;
    align-items: flex-end;
    gap: 24px;
    flex: 1;
}

.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 400px;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    background: #f8fafc;
    border: 1px solid var(--extranet-accent);
    border-radius: var(--extranet-rounded);
    padding: 4px;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--extranet-primary);
    background: white;
    box-shadow: 0 0 0 3px rgba(252, 102, 102, 0.05);
}

.form-control-search {
    border: none;
    background: transparent;
    padding: 8px 16px;
    width: 100%;
    font-size: 14px;
    color: #1e293b;
    outline: none;
}

.btn-search {
    background: var(--extranet-primary);
    border: none;
    width: 38px;
    height: 38px;
    border-radius: var(--extranet-rounded);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search:hover { filter: brightness(0.9); }

.filter-dropdown-group { min-width: 160px; }

.form-select-ext {
    width: 100%;
    padding: 10px 16px;
    background: #f8fafc;
    border: 1px solid var(--extranet-accent);
    border-radius: var(--extranet-rounded);
    font-size: 14px;
    color: #475569;
    font-weight: 500;
    outline: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
}

/* Stats Summary */
.filter-right { display: flex; align-items: center; gap: 16px; }
.stats-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: #f8faff;
    border-radius: var(--extranet-rounded);
    font-size: 13px;
    border: 1px solid #e0e7ff;
}
.stats-label { color: #64748b; font-weight: 600; }
.stats-value { color: var(--extranet-primary); font-weight: 800; }

.btn-reset-primary {
    width: 40px;
    height: 40px;
    border: 1px solid var(--extranet-accent);
    background: white;
    border-radius: var(--extranet-rounded);
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-reset-primary:hover {
    background: #f1f5f9;
    color: var(--extranet-primary);
    border-color: var(--extranet-primary);
}

/* Table */
.modern-list-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.modern-list-table thead th {
    padding: 18px 24px;
    background: #f8fafc;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 1px solid var(--extranet-accent);
}

.modern-list-table tbody td {
    padding: 20px 24px;
    vertical-align: middle;
    border-bottom: 1px solid var(--extranet-accent);
    color: #334155;
    font-size: 14px;
}

.modern-list-table tbody tr { transition: all 0.2s; }
.modern-list-table tbody tr:hover { background: #f8faff; }
.modern-list-table tbody tr.inactive-row { opacity: 0.6; }

.col-no { color: #94a3b8; font-weight: 600; text-align: center; font-size: 12px; }

/* Status Pills */
.status-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid transparent;
}

.status-dot { width: 6px; height: 6px; border-radius: 50%; }

.status-pill.active {
    background: #ecfdf5;
    color: #059669;
    border-color: #a7f3d0;
}
.status-pill.active .status-dot { background: #059669; }

.status-pill.inactive {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fecaca;
}
.status-pill.inactive .status-dot { background: #dc2626; }

.code-pill {
    padding: 4px 10px;
    background: #f1f5f9;
    border: 1px solid var(--extranet-accent);
    border-radius: 6px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    color: #475569;
}

.branch-name-box { display: flex; flex-direction: column; }
.branch-name { font-weight: 700; color: #1e293b; font-size: 15px; }
.profile-id { font-size: 10px; color: #94a3b8; letter-spacing: 0.5px; }

.address-box {
    max-width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #64748b;
    font-size: 13px;
}

.phone-text { font-weight: 600; color: #475569; font-size: 13px; }

/* Action Buttons */
.action-btn {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--extranet-accent);
    background: white;
    color: #64748b;
    transition: all 0.2s;
    cursor: pointer;
    text-decoration: none;
}

.action-btn.edit:hover { background: #eff6ff; color: #2563eb; border-color: #bfdbfe; }
.action-btn.delete:hover { background: #fef2f2; color: #dc2626; border-color: #fecaca; }

/* Empty State Enhancement */
.empty-list { padding: 100px 40px; text-align: center; }
.empty-art { font-size: 64px; color: #e2e8f0; margin-bottom: 24px; opacity: 1; }
.empty-art.filtered { color: #f1f5f9; }
.empty-art i { 
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%); 
    -webkit-background-clip: text; 
    background-clip: text;
    -webkit-text-fill-color: transparent; 
}
.empty-art.filtered i { 
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); 
    -webkit-background-clip: text; 
    background-clip: text;
    -webkit-text-fill-color: transparent; 
}

.empty-list h3 { font-size: 22px; font-weight: 800; color: #1e293b; margin-bottom: 12px; letter-spacing: -0.5px; }
.empty-list p { color: #94a3b8; font-size: 15px; max-width: 450px; margin: 0 auto; line-height: 1.6; }

.btn-clear-empty {
    padding: 12px 28px;
    background: white;
    border: 1px solid var(--extranet-accent);
    color: #64748b;
    border-radius: var(--extranet-rounded);
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
}

.btn-clear-empty:hover {
    background: #f8fafc;
    color: var(--extranet-primary);
    border-color: var(--extranet-primary);
    transform: translateY(-1px);
}

.btn-create-empty {
    padding: 14px 32px;
    background: var(--extranet-primary);
    color: white;
    border-radius: var(--extranet-rounded);
    font-weight: 700;
    font-size: 14px;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s;
    border: none;
    box-shadow: 0 4px 12px rgba(252, 102, 102, 0.2);
}

.btn-create-empty:hover {
    filter: brightness(0.95);
    transform: translateY(-1px);
    color: white;
}

/* Loading State */
.loading-state { padding: 80px 40px; text-align: center; }
.loader-box { display: flex; flex-direction: column; align-items: center; gap: 16px; }
.loader-box p { color: #64748b; font-weight: 500; font-size: 15px; }

/* Footer */
.card-footer {
    padding: 24px 32px;
    background: #ffffff;
    border-top: 1px solid #f1f5f9;
}

.alert-box-ext {
    padding: 16px 24px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: var(--extranet-rounded);
    color: #dc2626;
    font-weight: 600;
    font-size: 14px;
}

@media (max-width: 1200px) {
    .filter-bar { flex-direction: column; align-items: stretch; gap: 16px; }
    .filter-left { flex-direction: column; align-items: stretch; gap: 12px; }
}
</style>
